// The icon set, drawn rather than fetched.
//
// One 24x24 stroked grid, no icon font and no sprite request: an icon that
// arrives after the first paint is an icon that visibly pops in. Paths are the
// approved prototype's, unchanged.
//
// `symbol` on a section is user-chosen data, so an unknown name must not be an
// error - it falls back to a plain circle, which is also the column's default in
// migration 0001.

/** Also migration 0001's default for `section.symbol`. */
const CIRCLE = '<circle cx="12" cy="12" r="8.5"/>';

const PATHS: Record<string, string> = {
  briefcase:
    '<path d="M3 8.5h18v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-10Z"/><path d="M9 8.5V6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2.5"/><path d="M3 13h18"/>',
  book: '<path d="M4 4.8A1.8 1.8 0 0 1 5.8 3H19v14.5H5.8A1.8 1.8 0 0 0 4 19.3V4.8Z"/><path d="M4 19.3A1.8 1.8 0 0 1 5.8 17.5H19V21H5.8A1.8 1.8 0 0 1 4 19.3Z"/>',
  dumbbell: '<path d="M4 9v6M7.5 6.5v11M16.5 6.5v11M20 9v6M7.5 12h9"/>',
  music:
    '<path d="M9 17.5V5.8l10-1.8v11.5"/><circle cx="6.5" cy="17.5" r="2.5"/><circle cx="16.5" cy="15.5" r="2.5"/>',
  pot: '<path d="M4 10h16v4.5a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5V10Z"/><path d="M8.5 10V7M12 10V6M15.5 10V7"/><path d="M2.5 12.5h1.5M20 12.5h1.5"/>',
  leaf: '<path d="M4.5 20C4 12 9.5 5 20 4c.4 9.5-4.5 15-12 15H4.5Z"/><path d="M9 15.5c2-3.5 5-5.5 8-6.5"/>',
  heart:
    '<path d="M12 20.2S4.6 15.6 4.6 10.4A4.4 4.4 0 0 1 12 7.6a4.4 4.4 0 0 1 7.4 2.8c0 5.2-7.4 9.8-7.4 9.8Z"/>',
  home: '<path d="M3.8 11 12 4l8.2 7"/><path d="M6 10v9.5h12V10"/>',
  star: '<path d="m12 3.5 2.4 5.4 5.9.6-4.4 3.9 1.3 5.8L12 16.2l-5.2 3 1.3-5.8-4.4-3.9 5.9-.6L12 3.5Z"/>',
  palette:
    '<path d="M12 3.2a8.8 8.8 0 1 0 0 17.6h1.6a2.1 2.1 0 0 0 0-4.2H13a2.1 2.1 0 0 1 0-4.2h3.2a4.6 4.6 0 0 0 0-9.2H12Z"/><circle cx="7.6" cy="11" r=".9" fill="currentColor" stroke="none"/><circle cx="10" cy="7.4" r=".9" fill="currentColor" stroke="none"/><circle cx="14.6" cy="7.6" r=".9" fill="currentColor" stroke="none"/>',
  pencil:
    '<path d="M4 20h4.2L20.2 8a2.9 2.9 0 0 0-4.2-4.2L4 15.8V20Z"/><path d="m14.4 5.6 4.2 4.2"/>',
  clock: '<circle cx="12" cy="12" r="8.5"/><path d="M12 7.4V12l3 1.8"/>',
  circle: CIRCLE,

  /**
   * Reminders. The prototype's own glyph for them, unchanged
   * (`prototype.html`), and the only chip in the client that is not a
   * schedule's - which is the point: a schedule is identified by its hue, and a
   * reminder is the thing that has none.
   */
  pin: '<path d="M9 3.5h6l-.8 5.2 3.3 3.1v1.7H6.5v-1.7l3.3-3.1L9 3.5Z"/><path d="M12 13.5v7"/>',

  /**
   * A clock running backwards: the conventional history glyph, and the ONE
   * icon here the prototype does not have.
   *
   * The captain asked for it in these words - "a very tiny icon... like a time
   * clock but it's going in reverse" (2026-08-13) - for a reminder that was open
   * on the day you are looking at and has been finished since. It is `clock`
   * above with the circle opened at the top left and an arrow turning back into
   * the gap, so the two read as the same family at 15px on a meta line: the
   * same centre, the same radius, the same hands.
   */
  history:
    '<path d="M3.5 12a8.5 8.5 0 1 0 8.5-8.5 8.6 8.6 0 0 0-6.1 2.5L3.5 8.4"/><path d="M3.5 4.2v4.2h4.2"/><path d="M12 7.4V12l3 1.8"/>',

  moon: '<path d="M20.2 14.8A8.6 8.6 0 0 1 9.2 3.8a8.6 8.6 0 1 0 11 11Z"/>',
  // The Wording row in Settings, and the prototype's own path for it.
  speech:
    '<path d="M20 14.4a1.6 1.6 0 0 1-1.6 1.6H9.2L5 19.6V16h-.4A1.6 1.6 0 0 1 3 14.4V5.6A1.6 1.6 0 0 1 4.6 4h13.8A1.6 1.6 0 0 1 20 5.6Z"/>',
  today:
    '<rect x="3.5" y="5" width="17" height="15.5" rx="2.6"/><path d="M3.5 9.5h17M8 3.2v3.4M16 3.2v3.4"/><path d="m9 14.4 2 2 4-4"/>',
  archive:
    '<rect x="3.5" y="4" width="17" height="4.5" rx="1.4"/><path d="M5.2 8.5V19a1.5 1.5 0 0 0 1.5 1.5h10.6a1.5 1.5 0 0 0 1.5-1.5V8.5"/><path d="M10 12h4"/>',
  trash:
    '<path d="M4 6.5h16"/><path d="M9.5 6.5V4.6h5v1.9"/><path d="M6.4 6.5 7.5 20a1.5 1.5 0 0 0 1.5 1.4h6a1.5 1.5 0 0 0 1.5-1.4l1.1-13.5"/>',
  note: '<path d="M6 3.5h8.5L19 8v12.5H6a1.5 1.5 0 0 1-1.5-1.5V5A1.5 1.5 0 0 1 6 3.5Z"/><path d="M14 3.6V8h4.6M8.5 12.5h7M8.5 16h4.5"/>',

  // Realize. A topic with three things off it - the smallest picture of the
  // screen it opens, and the one glyph here (with `history`) the prototype does
  // not have, because the prototype predates the screen. The stubs stop at each
  // circle's edge rather than at its centre, so nothing shows through a node.
  realize:
    '<circle cx="12" cy="12" r="3.2"/><circle cx="19" cy="5.6" r="2.2"/><circle cx="5" cy="6.6" r="2.2"/><circle cx="12" cy="19.8" r="2.2"/><path d="m14.4 9.8 2.9-2.6M9.6 10.1 6.8 8.3M12 15.2v2.4"/>',
  search: '<circle cx="11" cy="11" r="7.5"/><path d="m21 21-4.4-4.4"/>',
  check: '<path d="m5 12.6 4.6 4.6L19 7.4"/>',
  chevD: '<path d="m6 9.5 6 6 6-6"/>',
  chevR: '<path d="m9.5 6 6 6-6 6"/>',
  chevL: '<path d="m14.5 6-6 6 6 6"/>',
  plus: '<path d="M12 5v14M5 12h14"/>',
  // The Export row in Settings, and the prototype's own path for it, unchanged.
  down: '<path d="M12 4.5v13M6.5 12 12 17.5 17.5 12"/>',
  x: '<path d="M6 6l12 12M18 6 6 18"/>',
  info: '<circle cx="12" cy="12" r="8.5"/><path d="M12 11v5.5M12 7.8h.01"/>',

  // The same calendar frame as `today` above, carrying days rather than a tick:
  // the two sit next to each other in the navigation, so they have to read as
  // one pair rather than as two unrelated glyphs.
  month:
    '<rect x="3.5" y="5" width="17" height="15.5" rx="2.6"/><path d="M3.5 9.5h17M8 3.2v3.4M16 3.2v3.4"/><path d="M7.8 13h.01M11.9 13h.01M16 13h.01M7.8 16.6h.01M11.9 16.6h.01"/>',

  // The navigation's own, plus the way out. `today` above is the first.
  grid: '<rect x="3.5" y="3.5" width="7" height="7" rx="1.6"/><rect x="13.5" y="3.5" width="7" height="7" rx="1.6"/><rect x="3.5" y="13.5" width="7" height="7" rx="1.6"/><rect x="13.5" y="13.5" width="7" height="7" rx="1.6"/>',
  // Columns of different heights, which is what a board looks like from across
  // the room - and what tells it apart from `grid`, the year retrospective's.
  board:
    '<rect x="3.5" y="4" width="4.6" height="16" rx="1.5"/><rect x="9.7" y="4" width="4.6" height="11" rx="1.5"/><rect x="15.9" y="4" width="4.6" height="14" rx="1.5"/>',
  gear: '<circle cx="12" cy="12" r="3.2"/><path d="M19.4 14.2a1.5 1.5 0 0 0 .3 1.7l.1.1a1.9 1.9 0 1 1-2.7 2.7l-.1-.1a1.5 1.5 0 0 0-2.6 1v.2a1.9 1.9 0 1 1-3.8 0v-.1a1.5 1.5 0 0 0-2.6-1l-.1.1a1.9 1.9 0 1 1-2.7-2.7l.1-.1a1.5 1.5 0 0 0-1-2.6H4a1.9 1.9 0 1 1 0-3.8h.1a1.5 1.5 0 0 0 1-2.6l-.1-.1a1.9 1.9 0 1 1 2.7-2.7l.1.1a1.5 1.5 0 0 0 2.6-1V4a1.9 1.9 0 1 1 3.8 0v.1a1.5 1.5 0 0 0 2.6 1l.1-.1a1.9 1.9 0 1 1 2.7 2.7l-.1.1a1.5 1.5 0 0 0 1 2.6h.2a1.9 1.9 0 1 1 0 3.8h-.1a1.5 1.5 0 0 0-1.4.9Z"/>',
  out: '<path d="M14 4.5h4.5A1.5 1.5 0 0 1 20 6v12a1.5 1.5 0 0 1-1.5 1.5H14"/><path d="M9.5 8 5.5 12l4 4M5.5 12H15"/>',
  /** A chain link: the share control, and the badge on a published note. */
  link: '<path d="M10 13.8a4 4 0 0 0 5.7 0l2.8-2.8a4 4 0 0 0-5.7-5.7l-1.4 1.4"/><path d="M14 10.2a4 4 0 0 0-5.7 0l-2.8 2.8a4 4 0 0 0 5.7 5.7l1.4-1.4"/>',

  // The daily-notification row in Settings, and the ONE glyph here that is not
  // the prototype's: it has no notification anywhere to take a path from. Drawn
  // on the same 24x24 grid at the same stroke weight as the rest, and quiet
  // rather than ringing - a bell with motion lines beside it would draw the one
  // thing this feature is defined against.
  bell: '<path d="M17.5 16.5V11a5.5 5.5 0 1 0-11 0v5.5L5 19h14l-1.5-2.5Z"/><path d="M10 19.4a2 2 0 0 0 4 0"/>',
};

/**
 * Own properties only: the name is user data, and a plain lookup would answer
 * 'toString' or 'constructor' with something inherited from `Object.prototype`,
 * so the fallback would never fire for exactly the names that need it.
 */
function pathFor(name: string): string {
  if (!Object.hasOwn(PATHS, name)) return CIRCLE;
  return PATHS[name] ?? CIRCLE;
}

export function Icon({ name }: { name: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      // The paths are constants in this file, never user input.
      dangerouslySetInnerHTML={{ __html: pathFor(name) }}
    />
  );
}

/** The wordmark's tick-in-a-page. Sized by `.brand-mark`, coloured by the theme. */
export function BrandMark() {
  return (
    <svg className="brand-mark" viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <rect
        x="3.5"
        y="2.5"
        width="25"
        height="27"
        rx="5"
        fill="var(--surface)"
        stroke="var(--ink)"
        strokeWidth="2"
      />
      <path
        d="M10 17.2 14 21.2 22.5 12"
        stroke="var(--ink)"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
